import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { CentralRiskEventBbox } from '../central-monitoring/centralRiskEventTypes';
import { getBboxBorderColor } from '../../utils/bboxOverlayColors';
import { formatBboxOverlayLabel } from '../../utils/composeEventSnapshot';
import {
  DEFAULT_RISK_BBOX_COLOR,
  hexToRgba,
  resolveRiskAppearance,
} from '../../constants/riskEventAppearance';
import { resolveManualRiskEvents } from '../../constants/scenarioRiskEvents';

export const RISK_BBOX_BORDER = DEFAULT_RISK_BBOX_COLOR;
export const RISK_BBOX_FILL = hexToRgba(DEFAULT_RISK_BBOX_COLOR, 0.18);

export type BboxStylePreset = {
  id: string;
  label: string;
  class_name: string;
  motionRisk: boolean;
  riskSubtypeLabel: string;
  /** 미리보기용 (실제 stroke는 motionRisk/class로 결정) */
  color: string;
};

const NON_RISK_PRESETS: BboxStylePreset[] = [
  {
    id: 'swimmer',
    label: '수영자',
    class_name: 'swimmer',
    motionRisk: false,
    riskSubtypeLabel: '수영자',
    color: '#00ff00',
  },
  {
    id: 'lifeguard',
    label: '구조요원',
    class_name: 'lifeguard',
    motionRisk: false,
    riskSubtypeLabel: '구조요원',
    color: '#00b0ff',
  },
  {
    id: 'person',
    label: '사람',
    class_name: 'person',
    motionRisk: false,
    riskSubtypeLabel: '사람',
    color: '#ffeb3b',
  },
];

/** 시설 프로필·CCTV 시나리오에 맞는 위험 종류 + 일반 클래스 */
export function buildBboxStylePresets(
  scenarioProfile?: string | null,
  detectionScenario?: string | null
): BboxStylePreset[] {
  const riskOpts = resolveManualRiskEvents({
    scenarioProfile,
    selectedCctvScenario: detectionScenario,
  });
  const riskPresets: BboxStylePreset[] = riskOpts.map((o) => {
    const app = resolveRiskAppearance(o.type);
    return {
      id: o.type,
      label: o.label,
      class_name: o.type === 'drowning' ? 'drowning' : 'person',
      motionRisk: true,
      riskSubtypeLabel: o.label,
      color: app.color,
    };
  });
  if (riskPresets.length === 0) {
    riskPresets.push({
      id: 'risk',
      label: '위험',
      class_name: 'person',
      motionRisk: true,
      riskSubtypeLabel: '위험',
      color: RISK_BBOX_BORDER,
    });
  }
  return [...riskPresets, ...NON_RISK_PRESETS];
}

/** @deprecated 시설 무관 기본 — buildBboxStylePresets 사용 */
export const BBOX_STYLE_PRESETS: BboxStylePreset[] = buildBboxStylePresets('pool');

/** 미적용/직접입력이면 '' — Select가 첫 항목으로 가짜 매칭되면 같은 종류 재선택이 안 됨 */
function matchPreset(b: CentralRiskEventBbox, presets: BboxStylePreset[]): string {
  const label = (b.riskSubtypeLabel || '').trim();
  const cls = (b.class_name || '').toLowerCase();
  const risk = Boolean(b.motionRisk);
  if (!label) {
    if (!risk) {
      const byCls = presets.find((p) => !p.motionRisk && p.class_name === cls);
      return byCls?.id || '';
    }
    return '';
  }
  const byLabel = presets.find((p) => p.riskSubtypeLabel === label || p.label === label);
  if (byLabel) return byLabel.id;
  const byId = presets.find((p) => p.id === cls && p.riskSubtypeLabel === label);
  if (byId) return byId.id;
  return '';
}

type DragMode =
  | { kind: 'create'; x0: number; y0: number }
  | { kind: 'move'; index: number; startX: number; startY: number; orig: number[] }
  | {
      kind: 'resize';
      index: number;
      handle: string;
      startX: number;
      startY: number;
      orig: number[];
    };

type Props = {
  imageUrl: string;
  bboxes: CentralRiskEventBbox[];
  onChange: (next: CentralRiskEventBbox[]) => void;
  defaultFrameWidth?: number;
  defaultFrameHeight?: number;
  maxHeight?: number;
  disabled?: boolean;
  defaultMotionRisk?: boolean;
  defaultClassName?: string;
  defaultLabel?: string;
  /** 시설 관제 프로필 — 이벤트 종류 목록 */
  scenarioProfile?: string | null;
  /** CCTV detection_scenario (있으면 우선) */
  detectionScenario?: string | null;
  /** 선택 bbox 인덱스 (부모에서 코드명 적용 대상) */
  selectedIndex?: number | null;
  onSelectedIndexChange?: (index: number | null) => void;
  /** 이벤트 종류를 박스에 적용했을 때 (코드명·색 입력란 동기화) */
  onEventTypeApplied?: (info: {
    type: string;
    label: string;
    color: string;
  }) => void;
};

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

function normalizeBox(x1: number, y1: number, x2: number, y2: number): number[] {
  return [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)];
}

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;

const EditableSnapshotBboxEditor: React.FC<Props> = ({
  imageUrl,
  bboxes,
  onChange,
  defaultFrameWidth = 1280,
  defaultFrameHeight = 720,
  maxHeight = 360,
  disabled = false,
  defaultMotionRisk = true,
  defaultClassName = 'person',
  defaultLabel,
  scenarioProfile,
  detectionScenario,
  selectedIndex: selectedIndexProp,
  onSelectedIndexChange,
  onEventTypeApplied,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragMode | null>(null);
  const [selectedInternal, setSelectedInternal] = useState<number | null>(null);
  const selected =
    selectedIndexProp !== undefined ? selectedIndexProp : selectedInternal;
  const setSelected = useCallback(
    (index: number | null) => {
      setSelectedInternal(index);
      onSelectedIndexChange?.(index);
    },
    [onSelectedIndexChange]
  );
  const [draftRect, setDraftRect] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);

  const stylePresets = useMemo(
    () => buildBboxStylePresets(scenarioProfile, detectionScenario),
    [scenarioProfile, detectionScenario]
  );

  const defaultPresetId =
    stylePresets.find((p) => p.motionRisk)?.id || stylePresets[0]?.id || 'person';
  const [drawPresetId, setDrawPresetId] = useState(defaultPresetId);

  useEffect(() => {
    if (!stylePresets.some((p) => p.id === drawPresetId)) {
      setDrawPresetId(defaultPresetId);
    }
  }, [stylePresets, drawPresetId, defaultPresetId]);

  /** 선택 없으면 마지막 그린 bbox에 적용 */
  const targetIndex = useMemo(() => {
    if (selected != null && selected >= 0 && selected < bboxes.length) return selected;
    if (bboxes.length > 0) return bboxes.length - 1;
    return null;
  }, [selected, bboxes.length]);

  const frameSize = useMemo(() => {
    const first = bboxes.find((b) => b.frame_width && b.frame_height);
    return {
      w: Math.max(1, Number(first?.frame_width) || naturalSize?.w || defaultFrameWidth),
      h: Math.max(1, Number(first?.frame_height) || naturalSize?.h || defaultFrameHeight),
    };
  }, [bboxes, naturalSize, defaultFrameWidth, defaultFrameHeight]);

  const clientToFrame = useCallback(
    (clientX: number, clientY: number) => {
      const el = containerRef.current;
      if (!el) return { x: 0, y: 0 };
      const r = el.getBoundingClientRect();
      const x = ((clientX - r.left) / Math.max(1, r.width)) * frameSize.w;
      const y = ((clientY - r.top) / Math.max(1, r.height)) * frameSize.h;
      return {
        x: clamp(x, 0, frameSize.w),
        y: clamp(y, 0, frameSize.h),
      };
    },
    [frameSize]
  );

  const updateBboxAt = useCallback(
    (index: number, patch: Partial<CentralRiskEventBbox> & { bbox?: number[] }) => {
      const next = bboxes.map((b, i) =>
        i === index
          ? {
              ...b,
              ...patch,
              frame_width: frameSize.w,
              frame_height: frameSize.h,
            }
          : b
      );
      onChange(next);
    },
    [bboxes, onChange, frameSize]
  );

  const applyPresetToSelected = (presetId: string) => {
    const p = stylePresets.find((x) => x.id === presetId);
    if (!p) return;
    setDrawPresetId(presetId);
    const idx = targetIndex;
    if (idx == null) return;
    setSelected(idx);
    updateBboxAt(idx, {
      class_name: p.class_name,
      motionRisk: p.motionRisk,
      riskSubtypeLabel: p.riskSubtypeLabel,
      borderColor: p.motionRisk ? p.color : undefined,
    });
    if (p.motionRisk) {
      onEventTypeApplied?.({
        type: p.id,
        label: p.riskSubtypeLabel || p.label,
        color: p.color,
      });
    }
  };

  const deleteSelected = useCallback(() => {
    if (selected == null || disabled) return;
    onChange(bboxes.filter((_, i) => i !== selected));
    setSelected(null);
  }, [selected, disabled, bboxes, onChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (disabled) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
          return;
        }
        if (t && t.tagName === 'SELECT') return;
        deleteSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteSelected, disabled]);

  const onPointerDownBg = (e: React.PointerEvent) => {
    if (disabled) return;
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const { x, y } = clientToFrame(e.clientX, e.clientY);
    dragRef.current = { kind: 'create', x0: x, y0: y };
    setSelected(null);
    setDraftRect(null);
  };

  const onPointerDownBox = (e: React.PointerEvent, index: number) => {
    if (disabled) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const track = bboxes[index];
    if (!track?.bbox || track.bbox.length < 4) return;
    const { x, y } = clientToFrame(e.clientX, e.clientY);
    dragRef.current = {
      kind: 'move',
      index,
      startX: x,
      startY: y,
      orig: [...track.bbox.map(Number)],
    };
    setSelected(index);
    setDrawPresetId(matchPreset(track, stylePresets));
  };

  const onPointerDownHandle = (e: React.PointerEvent, index: number, handle: string) => {
    if (disabled) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const track = bboxes[index];
    if (!track?.bbox || track.bbox.length < 4) return;
    const { x, y } = clientToFrame(e.clientX, e.clientY);
    dragRef.current = {
      kind: 'resize',
      index,
      handle,
      startX: x,
      startY: y,
      orig: [...track.bbox.map(Number)],
    };
    setSelected(index);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const { x, y } = clientToFrame(e.clientX, e.clientY);
    if (drag.kind === 'create') {
      const [x1, y1, x2, y2] = normalizeBox(drag.x0, drag.y0, x, y);
      setDraftRect({
        left: (x1 / frameSize.w) * 100,
        top: (y1 / frameSize.h) * 100,
        width: ((x2 - x1) / frameSize.w) * 100,
        height: ((y2 - y1) / frameSize.h) * 100,
      });
      return;
    }
    if (drag.kind === 'move') {
      const dx = x - drag.startX;
      const dy = y - drag.startY;
      let [x1, y1, x2, y2] = drag.orig;
      const w = x2 - x1;
      const h = y2 - y1;
      x1 = clamp(x1 + dx, 0, frameSize.w - w);
      y1 = clamp(y1 + dy, 0, frameSize.h - h);
      updateBboxAt(drag.index, { bbox: [x1, y1, x1 + w, y1 + h] });
      return;
    }
    if (drag.kind === 'resize') {
      let [x1, y1, x2, y2] = drag.orig;
      const h = drag.handle;
      if (h.includes('w')) x1 = clamp(x, 0, x2 - 4);
      if (h.includes('e')) x2 = clamp(x, x1 + 4, frameSize.w);
      if (h.includes('n')) y1 = clamp(y, 0, y2 - 4);
      if (h.includes('s')) y2 = clamp(y, y1 + 4, frameSize.h);
      updateBboxAt(drag.index, { bbox: normalizeBox(x1, y1, x2, y2) });
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    dragRef.current = null;
    setDraftRect(null);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (!drag || drag.kind !== 'create') return;
    const { x, y } = clientToFrame(e.clientX, e.clientY);
    const box = normalizeBox(drag.x0, drag.y0, x, y);
    if (box[2] - box[0] < 8 || box[3] - box[1] < 8) return;
    // 먼저 박스만 그림 — 코드명·색·이벤트종류는 이후 이 박스에 적용
    const next: CentralRiskEventBbox = {
      bbox: box,
      frame_width: frameSize.w,
      frame_height: frameSize.h,
      motionRisk: true,
      class_name: defaultClassName || 'person',
      riskSubtypeLabel: '',
      riskDescription: '',
      borderColor: undefined,
      trackId: Date.now() % 100000,
    };
    const nextList = [...bboxes, next];
    onChange(nextList);
    setSelected(nextList.length - 1);
  };

  const selectedPresetId =
    targetIndex != null && bboxes[targetIndex]
      ? matchPreset(bboxes[targetIndex], stylePresets)
      : drawPresetId;

  return (
    <Box>
      {!disabled ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            mb: 1,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="bbox-type-label" shrink>
              이벤트 종류
            </InputLabel>
            <Select
              labelId="bbox-type-label"
              label="이벤트 종류"
              displayEmpty
              notched
              value={
                stylePresets.some((p) => p.id === selectedPresetId) ? selectedPresetId : ''
              }
              onChange={(e) => {
                const v = String(e.target.value);
                if (!v) return;
                applyPresetToSelected(v);
              }}
              MenuProps={{ disablePortal: true }}
              disabled={bboxes.length === 0}
              renderValue={(v) => {
                if (!v) return '종류 선택…';
                const p = stylePresets.find((x) => x.id === v);
                return p?.label || String(v);
              }}
            >
              <MenuItem value="" disabled>
                종류 선택…
              </MenuItem>
              {stylePresets.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: 0.5,
                        bgcolor: p.motionRisk ? hexToRgba(p.color, 0.25) : 'transparent',
                        border: p.motionRisk
                          ? `3px solid ${p.color}`
                          : `2px solid ${p.color}`,
                        flexShrink: 0,
                      }}
                    />
                    {p.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {bboxes.length === 0
              ? '먼저 캡처 위에 드래그해 bbox 작성'
              : targetIndex != null
                ? `선택 #${targetIndex + 1}에 이벤트 종류·코드명 적용`
                : '박스를 선택'}
          </Typography>
          <ButtonGroup size="small" variant="outlined">
            <Button
              disabled={selected == null}
              color="error"
              onClick={deleteSelected}
            >
              선택 삭제
            </Button>
            <Button
              disabled={bboxes.length === 0}
              onClick={() => {
                onChange([]);
                setSelected(null);
              }}
            >
              전체 삭제
            </Button>
          </ButtonGroup>
        </Box>
      ) : null}
      <Box
        ref={containerRef}
        onPointerDown={onPointerDownBg}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          position: 'relative',
          display: 'inline-block',
          maxWidth: '100%',
          bgcolor: '#111',
          borderRadius: 1,
          overflow: 'hidden',
          lineHeight: 0,
          cursor: disabled ? 'default' : 'crosshair',
          userSelect: 'none',
          touchAction: 'none',
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt="이벤트 캡처"
          onLoad={(ev) => {
            const img = ev.currentTarget;
            setNaturalSize({
              w: img.naturalWidth || defaultFrameWidth,
              h: img.naturalHeight || defaultFrameHeight,
            });
          }}
          draggable={false}
          sx={{
            maxWidth: '100%',
            maxHeight,
            width: 'auto',
            height: 'auto',
            display: 'block',
            pointerEvents: 'none',
          }}
        />
        {bboxes.map((track, idx) => {
          if (!Array.isArray(track.bbox) || track.bbox.length < 4) return null;
          const fw = Math.max(1, Number(track.frame_width) || frameSize.w);
          const fh = Math.max(1, Number(track.frame_height) || frameSize.h);
          let [x1, y1, x2, y2] = track.bbox.map(Number);
          const left = (Math.min(x1, x2) / fw) * 100;
          const top = (Math.min(y1, y2) / fh) * 100;
          const width = (Math.abs(x2 - x1) / fw) * 100;
          const height = (Math.abs(y2 - y1) / fh) * 100;
          const atRisk = Boolean(track.motionRisk);
          const border = atRisk
            ? String(track.borderColor || '').trim() || RISK_BBOX_BORDER
            : getBboxBorderColor(track.class_name || '');
          const label = formatBboxOverlayLabel(track);
          const isSel = selected === idx;
          return (
            <Box
              key={`edit-bbox-${track.trackId ?? idx}`}
              onPointerDown={(e) => onPointerDownBox(e, idx)}
              sx={{
                position: 'absolute',
                left: `${left}%`,
                top: `${top}%`,
                width: `${width}%`,
                height: `${height}%`,
                border: atRisk ? `3px solid ${border}` : `2px solid ${border}`,
                backgroundColor: atRisk ? hexToRgba(border, 0.18) : 'transparent',
                boxShadow: atRisk ? `0 0 8px ${border}` : isSel ? '0 0 0 1px #fff' : 'none',
                outline: isSel ? '1px dashed #fff' : 'none',
                boxSizing: 'border-box',
                cursor: disabled ? 'default' : 'move',
                pointerEvents: disabled ? 'none' : 'auto',
              }}
            >
              {label ? (
                <Typography
                  component="span"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    // 박스 안쪽 상단 — 부모 overflow:hidden 에 잘리지 않음
                    top: 0,
                    maxWidth: '100%',
                    px: 0.5,
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: '16px',
                    color: atRisk ? border : '#eee',
                    bgcolor: atRisk ? 'rgba(0,0,0,0.78)' : 'rgba(0,0,0,0.6)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    pointerEvents: 'none',
                    zIndex: 3,
                  }}
                  title={label}
                >
                  {label}
                </Typography>
              ) : null}
              {!disabled && isSel
                ? HANDLES.map((h) => {
                    const style: React.CSSProperties = {
                      position: 'absolute',
                      width: 10,
                      height: 10,
                      background: '#fff',
                      border: `2px solid ${atRisk ? border : '#90caf9'}`,
                      boxSizing: 'border-box',
                      zIndex: 2,
                    };
                    if (h.includes('n')) style.top = -5;
                    if (h.includes('s')) style.bottom = -5;
                    if (h.includes('w')) style.left = -5;
                    if (h.includes('e')) style.right = -5;
                    if (h === 'n' || h === 's') {
                      style.left = '50%';
                      style.marginLeft = -5;
                    }
                    if (h === 'e' || h === 'w') {
                      style.top = '50%';
                      style.marginTop = -5;
                    }
                    const cursors: Record<string, string> = {
                      nw: 'nwse-resize',
                      se: 'nwse-resize',
                      ne: 'nesw-resize',
                      sw: 'nesw-resize',
                      n: 'ns-resize',
                      s: 'ns-resize',
                      e: 'ew-resize',
                      w: 'ew-resize',
                    };
                    style.cursor = cursors[h];
                    return (
                      <Box
                        key={h}
                        component="span"
                        onPointerDown={(ev) => onPointerDownHandle(ev, idx, h)}
                        style={style}
                      />
                    );
                  })
                : null}
            </Box>
          );
        })}
        {draftRect ? (
          <Box
            sx={{
              position: 'absolute',
              left: `${draftRect.left}%`,
              top: `${draftRect.top}%`,
              width: `${draftRect.width}%`,
              height: `${draftRect.height}%`,
              border: `2px dashed ${RISK_BBOX_BORDER}`,
              backgroundColor: 'transparent',
              boxSizing: 'border-box',
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default EditableSnapshotBboxEditor;
